import { useEffect, useState } from 'react';
import { Icon } from './icons';

/** How long a revealed value stays on screen before it masks itself again. */
export const REVEAL_SECONDS = 60;

/**
 * A sensitive detail, masked until the person working the record asks to see it. The app
 * fetches the real value on Show (the server records every reveal) and it masks itself again
 * after a minute or on Hide.
 */
export function RevealValue({
  masked,
  label,
  reveal,
}: {
  masked: string | null;
  /** What it is, for the button's name: "phone", "date of birth". */
  label: string;
  reveal: () => Promise<string | null>;
}) {
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (value === null) return;
    const t = setTimeout(() => setValue(null), REVEAL_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [value]);
  if (!masked) return <>—</>;
  const shown = value !== null;
  return (
    <span className="reveal">
      <span className={shown ? 'reveal-v' : 'reveal-v masked'} aria-live="polite">
        {shown ? value : masked}
      </span>
      <button
        type="button"
        className="reveal-b"
        aria-label={shown ? `Hide ${label}` : `Show ${label}`}
        aria-pressed={shown}
        disabled={busy}
        onClick={async () => {
          if (shown) return setValue(null);
          setBusy(true);
          setError(false);
          try {
            setValue((await reveal()) ?? '—');
          } catch {
            setError(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Icon.Eye />
        {shown ? 'Hide' : 'Show'}
      </button>
      {error && <span className="reveal-e">Could not load</span>}
    </span>
  );
}
